import {
  useFormErrorNotification,
  useFormSuccessNotification,
} from "@babackapp/react-hook-forms-components/form-notifications";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { fetchClient, workerFetchClient } from "../generated/client";
import type { paths } from "../generated/schema";
import type {
  HttpMethod,
  MediaType,
  PathsWithMethod,
} from "openapi-typescript-helpers";
import type { FetchResponse, MaybeOptionalInit } from "openapi-fetch";

type MutMethod = "post" | "put" | "patch" | "delete";

type InitFor<
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
> = MaybeOptionalInit<paths[P], M>;

// paths[P][M] may be undefined for some method+path combos at the generic level,
// so we intersect with Record<string, any> to satisfy FetchResponse's constraint.
type Operation<
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
> = paths[P][M] & Record<string, unknown>;

type DataFor<
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
> = Required<FetchResponse<Operation<M, P>, InitFor<M, P>, MediaType>>["data"];

type ErrorFor<
  M extends HttpMethod,
  P extends PathsWithMethod<paths, M>,
> = Required<FetchResponse<Operation<M, P>, InitFor<M, P>, MediaType>>["error"];

export interface UseApiMutationOptions<TData, TVariables> {
  invalidate?: (() => void) | (() => void)[];
  notify?: boolean | { success?: boolean; error?: boolean };
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: unknown, variables: TVariables, context?: unknown) => void;
  onMutate?: (variables: TVariables) => Promise<unknown> | unknown;
  onSettled?: (
    data: TData | undefined,
    error: unknown,
    variables: TVariables,
    context?: unknown,
  ) => Promise<unknown> | unknown;
  mapVariables?: (variables: TVariables) => unknown | Promise<unknown>;
  meta?: Record<string, unknown>;
  /** Use the worker server (VITE_API_WORKER_URL) for this mutation */
  useWorkerClient?: boolean;
}

function shouldNotify(
  option: boolean | { success?: boolean; error?: boolean } | undefined,
  type: "success" | "error",
): boolean {
  if (option === undefined || option === true) return true;
  if (option === false) return false;
  return option[type] !== false;
}

export function useApiMutation<
  Method extends MutMethod,
  Path extends PathsWithMethod<paths, Method>,
  TVariables = InitFor<Method, Path>,
>(
  method: Method,
  path: Path,
  options?: UseApiMutationOptions<DataFor<Method, Path>, TVariables>,
): UseMutationResult<
  DataFor<Method, Path>,
  ErrorFor<Method, Path>,
  TVariables
> {
  const successNotification = useFormSuccessNotification();
  const errorNotification = useFormErrorNotification();

  const notifySuccess = shouldNotify(options?.notify, "success");
  const notifyError = shouldNotify(options?.notify, "error");

  return useMutation({
    meta: options?.meta,
    onMutate: options?.onMutate,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(
        data as DataFor<Method, Path> | undefined,
        error as unknown,
        variables,
        context,
      );
    },
    onSuccess: (data: DataFor<Method, Path>, variables: TVariables) => {
      if (options?.invalidate) {
        const fns = Array.isArray(options.invalidate)
          ? options.invalidate
          : [options.invalidate];
        for (const fn of fns) fn();
      }
      if (notifySuccess) successNotification();
      options?.onSuccess?.(data, variables);
    },
    onError: (error, variables, context) => {
      if (notifyError) errorNotification();
      options?.onError?.(error as unknown, variables as TVariables, context);
    },
    mutationFn: async (variables: TVariables) => {
      const init = options?.mapVariables
        ? await options.mapVariables(variables)
        : variables;
      const uppercaseMethod = method.toUpperCase() as Uppercase<Method>;
      const client = options?.useWorkerClient ? workerFetchClient : fetchClient;
      const fetchFn = client[uppercaseMethod] as (
        url: Path,
        init: unknown,
      ) => Promise<{ data?: unknown; error?: unknown }>;
      const { data, error } = await fetchFn(path, init);
      if (error) throw error;
      return data as DataFor<Method, Path>;
    },
  }) as UseMutationResult<
    DataFor<Method, Path>,
    ErrorFor<Method, Path>,
    TVariables
  >;
}
